import { toaster } from '@/shared/ui/toaster.component';
import { SelectFilterField } from '@/shared/ui';
import { Page, PageHeader } from '@/shared/ui/page';
import { f, formatDateTimeToBrazil, useFilters } from '@/shared/utils';
import { Button, DataTable, Tag } from '@noorden-insurtech/alba-design-system';
import { useNavigate } from '@tanstack/react-router';
import { RefreshCw } from 'lucide-react';
import { useMemo } from 'react';
import type { NotificationStatusFilter } from '@/services/notifications/types';
import {
  toNotificationViewModel,
  type NotificationViewModel,
} from '../config/notification-presenters';
import { NOTIFICATION_TONE_CLASSES } from '../config/notification-tone';
import {
  useMarkAllNotificationsAsRead,
  useMarkNotificationAsRead,
  useNotificationTypes,
  useNotifications,
  useNotificationsScope,
  useUnreadNotificationsCount,
} from '../hooks';
import { createNotificationsColumns } from './notifications-table-columns';

const PAGE_SIZE = 20;

const statusOptions = [
  { label: 'Todas', value: 'all' },
  { label: 'Não lidas', value: 'unread' },
  { label: 'Lidas', value: 'read' },
];

/**
 * Tela de notificações.
 *
 * Pagina por CURSOR ("Carregar mais") e não por número de página, ao contrário
 * das demais listagens do portal: o DynamoDB não entrega contagem total sem
 * varrer a partição, e o filtro por tipo roda depois do limite da query — o
 * total seria um chute. O precedente no repo é o histórico de atividades da
 * cotação.
 */
export function NotificationsContent() {
  const navigate = useNavigate();
  const { enabled, partyCode, canMarkAsRead } = useNotificationsScope();

  // Sem `pageNo`/`pageSize`: não há página numerada aqui, e o reset automático
  // do useFilters depende justamente da chave `pageNo`.
  const { filters, setFilters, registerFilter } = useFilters({
    type: f.string().withDefault(''),
    status: f.string().withDefault('all'),
  });

  const { data: types } = useNotificationTypes();
  const markAsRead = useMarkNotificationAsRead();
  const markAllAsRead = useMarkAllNotificationsAsRead();

  const query = useNotifications(
    {
      type: filters.type || undefined,
      status: (filters.status || 'all') as NotificationStatusFilter,
      partyCode,
    },
    { limit: PAGE_SIZE, enabled }
  );

  // O filtro de tipo é montado pelo catálogo da API: um tipo novo no back
  // aparece aqui sem deploy de front.
  const typeOptions = useMemo(
    () => [
      { label: 'Todos os tipos', value: '' },
      ...(types ?? []).map(type => ({ label: type.label, value: type.type })),
    ],
    [types]
  );

  const typeLabels = useMemo(
    () =>
      Object.fromEntries((types ?? []).map(type => [type.type, type.label])),
    [types]
  );

  const notifications = useMemo<NotificationViewModel[]>(
    () =>
      (query.data?.pages.flatMap(page => page.items) ?? []).map(item =>
        toNotificationViewModel(item, formatDateTimeToBrazil)
      ),
    [query.data]
  );

  // A contagem vem do mesmo query key do sino (o React Query dedupe), e não da
  // página carregada: com o filtro em "Lidas", ou com as não lidas ainda em
  // páginas seguintes, olhar só a lista desabilitaria o botão sem motivo.
  const { data: unread } = useUnreadNotificationsCount(enabled, partyCode);
  const hasUnread = canMarkAsRead && (unread?.count ?? 0) > 0;

  function handleAction(_action: 'open', notification: NotificationViewModel) {
    handleOpen(notification);
  }

  function handleOpen(notification: NotificationViewModel) {
    // A seguradora só acompanha o feed da corretora — ver não pode dar por lido.
    if (canMarkAsRead && !notification.read) {
      // Mesmo motivo do sino: falha silenciosa aqui é um clique que navega e
      // deixa o badge como estava, sem explicação.
      markAsRead.mutate(notification.id, {
        onError: error => {
          toaster.create({
            type: 'error',
            title: 'Não foi possível marcar como lida',
            description:
              error instanceof Error
                ? error.message
                : 'Tente novamente em instantes.',
          });
        },
      });
    }
    if (notification.target) {
      void navigate(notification.target);
    }
  }

  const columns = useMemo(
    () => createNotificationsColumns(handleAction, typeLabels),
    // `handleAction` é estável o bastante: depende só de mutations e do
    // navigate, ambos referências fixas dentro da vida do componente.

    [typeLabels]
  );

  if (!enabled) {
    return (
      <Page.Content className='min-w-0'>
        <div className='rounded-3xl bg-white p-6 text-center text-alba-gray-600 shadow-[0px_4px_24px_rgba(15,22,30,0.08)]'>
          Selecione uma corretora no topo da página para ver as notificações
          dela.
        </div>
      </Page.Content>
    );
  }

  return (
    <>
      <PageHeader.Filters>
        <div className='flex flex-col gap-3 md:flex-1 md:flex-row md:items-center md:gap-4 lg:max-w-5xl'>
          <SelectFilterField
            {...registerFilter('type')}
            items={typeOptions}
            placeholder='Tipo'
            className='w-full md:min-w-[220px]'
            onValueChange={value => setFilters({ ...filters, type: value })}
          />
          <SelectFilterField
            {...registerFilter('status')}
            items={statusOptions}
            placeholder='Status'
            className='w-full md:min-w-[200px]'
            onValueChange={value => setFilters({ ...filters, status: value })}
          />
          <div className='flex items-center gap-3 md:contents'>
            {canMarkAsRead && (
              <Button
                variant='secondary'
                size='small'
                disabled={!hasUnread}
                loading={markAllAsRead.isPending}
                onClick={() => markAllAsRead.mutate()}
              >
                Marcar todas como lidas
              </Button>
            )}
            <button
              type='button'
              onClick={() => void query.refetch()}
              disabled={query.isFetching}
              className='flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-alba-gray-300 bg-white text-alba-gray-600 transition-colors hover:bg-alba-gray-50 hover:text-alba-gray-700 disabled:opacity-50'
              title='Atualizar'
            >
              <RefreshCw
                className={`h-4 w-4 ${query.isFetching ? 'animate-spin' : ''}`}
              />
            </button>
          </div>
        </div>
      </PageHeader.Filters>

      <Page.Content className='min-w-0'>
        <div className='space-y-4 md:hidden'>
          {query.isLoading
            ? Array.from({ length: 3 }).map((_, index) => (
                <div
                  key={index}
                  className='h-40 animate-pulse rounded-3xl bg-alba-gray-200'
                />
              ))
            : notifications.map(notification => (
                <article
                  key={notification.id}
                  className='rounded-3xl bg-white p-4 shadow-[0px_4px_24px_rgba(15,22,30,0.08)]'
                >
                  <div className='flex items-start justify-between gap-3'>
                    <Tag.Root
                      variant='outline'
                      colorScheme={
                        NOTIFICATION_TONE_CLASSES[notification.tone]
                          .tagColorScheme
                      }
                      size='sm'
                    >
                      <Tag.Label>
                        {typeLabels[notification.type] ?? notification.type}
                      </Tag.Label>
                    </Tag.Root>
                    <Tag.Root
                      variant='outline'
                      colorScheme={notification.read ? 'gray' : 'blue'}
                      size='sm'
                    >
                      <Tag.Label>
                        {notification.read ? 'Lida' : 'Não lida'}
                      </Tag.Label>
                    </Tag.Root>
                  </div>

                  <p className='mt-3 font-gotham font-bold text-alba-black-500'>
                    {notification.title}
                  </p>
                  <p className='mt-1 text-sm text-alba-gray-700'>
                    {notification.description}
                  </p>

                  <div className='mt-4 space-y-2'>
                    <div className='flex items-center justify-between gap-3'>
                      <p className='text-sm font-semibold text-alba-black-500'>
                        Data
                      </p>
                      <p className='truncate text-sm text-alba-gray-700'>
                        {notification.createdAtLabel}
                      </p>
                    </div>
                    <div className='flex items-center justify-between gap-3'>
                      <p className='text-sm font-semibold text-alba-black-500'>
                        Leitura
                      </p>
                      <p className='truncate text-sm text-alba-gray-700'>
                        {notification.readAtLabel ?? '—'}
                      </p>
                    </div>
                  </div>

                  <Button
                    variant='secondary'
                    size='small'
                    className='mt-4 w-full'
                    onClick={() => handleOpen(notification)}
                  >
                    Abrir
                  </Button>
                </article>
              ))}

          {!query.isLoading && notifications.length === 0 && (
            <div className='rounded-3xl bg-white p-6 text-center text-alba-gray-600 shadow-[0px_4px_24px_rgba(15,22,30,0.08)]'>
              Nenhuma notificação encontrada.
            </div>
          )}
        </div>

        <div className='hidden min-w-0 overflow-hidden rounded-3xl md:block'>
          <DataTable
            className='min-w-0'
            loading={query.isLoading}
            columns={columns}
            data={notifications}
            pageSize={PAGE_SIZE}
            emptyMessage='Nenhuma notificação encontrada.'
          />
        </div>

        {/* Quem decide se há mais é o cursor, nunca a quantidade de itens: uma
            página pode voltar curta porque o filtro roda depois do limite. */}
        {query.hasNextPage && (
          <div className='flex justify-center pt-4'>
            <Button
              variant='secondary'
              size='sm'
              loading={query.isFetchingNextPage}
              onClick={() => void query.fetchNextPage()}
            >
              Carregar mais
            </Button>
          </div>
        )}
      </Page.Content>
    </>
  );
}
